// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import type { CanonicalUnit } from "@bitebalance/contracts"
import { useI18n } from "@/i18n"
import { unitLabel } from "@/lib/units"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"

const UNITS: CanonicalUnit[] = ["g", "ml", "count"]

/**
 * "Add manually," made structured (Kitchen redesign, 2026-09-14 — web twin
 * of mobile/src/components/pantry/ManualEntrySheet.tsx). The quick-add
 * textarea used to be the ONLY manual path, with quantity/unit only
 * appearing a step later in the confirm sheet. Item + quantity + unit + an
 * optional expiry date are now the primary, fast form here — still one
 * sheet, still one submit. It produces the same plain-text description
 * ("6 count Eggs") the extraction endpoint already parses, so nothing
 * server-side changes: this is a friendlier front end onto the exact
 * pipeline free-text quick-add always used, still finishing at the same
 * confirmation step (spec: preview before it persists).
 *
 * "Type a list instead" keeps the original free-text path in the same
 * sheet, for the genuinely different job of adding several items in one
 * sentence.
 */
export interface ManualEntrySheetProps {
  extracting: boolean
  /** sourceText is what extraction reads; expiryDate (if set) carries through to the confirm sheet. */
  onSubmit: (sourceText: string, expiryDate: string | null) => void
  onCancel: () => void
}

export function ManualEntrySheet({ extracting, onSubmit, onCancel }: ManualEntrySheetProps) {
  const { t } = useI18n()
  const [mode, setMode] = useState<"structured" | "freeText">("structured")
  const [name, setName] = useState("")
  const [quantity, setQuantity] = useState("1")
  const [unit, setUnit] = useState<CanonicalUnit>("count")
  const [showExpiry, setShowExpiry] = useState(false)
  const [expiryDate, setExpiryDate] = useState("")
  const [freeText, setFreeText] = useState("")

  const structuredValid =
    name.trim().length > 0 && Number.isFinite(Number(quantity)) && Number(quantity) > 0
  const canSubmit = mode === "structured" ? structuredValid : freeText.trim().length > 0

  function submit() {
    if (!canSubmit) return
    if (mode === "structured") {
      onSubmit(`${quantity} ${unit} ${name.trim()}`.trim(), expiryDate.trim() || null)
    } else {
      onSubmit(freeText.trim(), null)
    }
  }

  return (
    <Sheet open onOpenChange={next => !next && onCancel()}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t("views.pantry.manualEntryTitle")}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col gap-4 px-4 pb-4">
          <div className="flex gap-2" role="tablist">
            <Button
              type="button"
              variant={mode === "structured" ? "default" : "outline"}
              className="min-h-9 flex-1"
              aria-pressed={mode === "structured"}
              onClick={() => setMode("structured")}
            >
              {t("views.pantry.manualModeStructured")}
            </Button>
            <Button
              type="button"
              variant={mode === "freeText" ? "default" : "outline"}
              className="min-h-9 flex-1"
              aria-pressed={mode === "freeText"}
              onClick={() => setMode("freeText")}
            >
              {t("views.pantry.manualModeFreeText")}
            </Button>
          </div>

          {mode === "structured" ? (
            <div className="flex flex-col gap-3">
              <label className="flex flex-col gap-1 text-sm text-ink-2">
                {t("views.pantry.nameLabel")}
                <Input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder={t("views.pantry.namePlaceholder")}
                  autoFocus
                />
              </label>
              <div className="flex items-end gap-2">
                <label className="flex flex-col gap-1 text-sm text-ink-2">
                  {t("views.pantry.quantityLabel")}
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    className="w-24"
                    value={quantity}
                    onChange={e => setQuantity(e.target.value)}
                  />
                </label>
                <select
                  className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
                  value={unit}
                  onChange={e => setUnit(e.target.value as CanonicalUnit)}
                >
                  {UNITS.map(u => (
                    <option key={u} value={u}>
                      {unitLabel(u, t)}
                    </option>
                  ))}
                </select>
              </div>
              {showExpiry ? (
                <label className="flex items-center gap-2 text-sm text-ink-2">
                  {t("views.pantry.expiryOptionalLabel")}
                  <Input
                    type="date"
                    className="w-40"
                    min={new Date().toISOString().slice(0, 10)}
                    value={expiryDate}
                    onChange={e => setExpiryDate(e.target.value)}
                  />
                </label>
              ) : (
                <button
                  type="button"
                  className="self-start text-xs font-semibold text-green underline"
                  onClick={() => setShowExpiry(true)}
                >
                  {t("views.pantry.addExpiryButton")}
                </button>
              )}
            </div>
          ) : (
            <label className="flex flex-col gap-2 text-sm font-medium text-ink">
              {t("views.pantry.quickAddLabel")}
              <Textarea
                placeholder={t("views.pantry.quickAddPlaceholder")}
                value={freeText}
                onChange={e => setFreeText(e.target.value)}
              />
            </label>
          )}
        </div>
        <SheetFooter>
          <Button
            type="button"
            className="min-h-11"
            disabled={!canSubmit || extracting}
            onClick={submit}
          >
            {extracting ? t("views.pantry.extracting") : t("views.pantry.manualEntrySubmit")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="min-h-11"
            disabled={extracting}
            onClick={onCancel}
          >
            {t("views.pantry.cancelButton")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
